import {
  isCreativeOnly,
  isEditorialOnly,
  type SearchParameters,
} from '../searchParameters';
import type { SearchProfile } from '../searchProfile';
import { SingleSeatFamilyEnum, type SingleSeatOffer } from './singleSeatOffer';

// Which single-seat messaging to show: one family's, or the one covering both.
export enum SingleSeatMerchandisingVariantEnum {
  CREATIVE = 'creative',
  EDITORIAL = 'editorial',
  CREATIVE_AND_EDITORIAL = 'creativeAndEditorial',
}

// The GettySingleSeatSubscription experiment, as far as eligibility needs it.
export interface SingleSeatExperiment {
  isOfferActive(offer: string): boolean;
}

// Anonymous visitors see single-seat merchandising only in these experiment arms.
const EXPERIMENT_OFFERS = ['ExpA', 'ExpB'];

// Whether the visitor is in the single-seat audience. Authenticated users must be offered a
// single-seat family and hold no royalty-free subscription; a profile that does not say whether
// they hold one is not eligible. Anonymous visitors must be in an experiment arm.
export const isEligibleForSingleSeat = (
  searchProfile: SearchProfile,
  offer: SingleSeatOffer,
  experiment: SingleSeatExperiment
): boolean => {
  if (searchProfile.isAuthenticated()) {
    return offer.families.size > 0 && searchProfile.hasRfSubscription === false;
  }
  return EXPERIMENT_OFFERS.some((arm) => experiment.isOfferActive(arm));
};

// A search with no family filter covers both families, so covering creative means "not
// editorial-only" rather than "includes creative".
const searchCovers = (
  searchParameters: SearchParameters,
  family: SingleSeatFamilyEnum
) =>
  family === SingleSeatFamilyEnum.CREATIVE
    ? !isEditorialOnly(searchParameters)
    : !isCreativeOnly(searchParameters);

const VARIANT_BY_FAMILY: Record<
  SingleSeatFamilyEnum,
  SingleSeatMerchandisingVariantEnum
> = {
  [SingleSeatFamilyEnum.CREATIVE]: SingleSeatMerchandisingVariantEnum.CREATIVE,
  [SingleSeatFamilyEnum.EDITORIAL]:
    SingleSeatMerchandisingVariantEnum.EDITORIAL,
};

// Which single-seat messaging to show, or null to show nothing. It is the intersection of what the
// visitor is searching with what the offer covers: both families gives CREATIVE_AND_EDITORIAL,
// exactly one gives that family, none gives null. So France (creative only) shows creative on
// creative and mixed searches and nothing on editorial-only searches.
export const getSingleSeatMerchandisingVariant = (
  searchParameters: SearchParameters,
  offer: SingleSeatOffer
): SingleSeatMerchandisingVariantEnum | null => {
  const shown = [...offer.families].filter((family) =>
    searchCovers(searchParameters, family)
  );

  if (shown.length > 1) {
    return SingleSeatMerchandisingVariantEnum.CREATIVE_AND_EDITORIAL;
  }
  return shown.length === 1 ? VARIANT_BY_FAMILY[shown[0]] : null;
};

// The single-seat messaging to show this visitor, or null when they are outside the audience or
// the offer does not cover their search. Every surface that merchandises single-seat asks this, so
// the banner and the in-grid tile agree on when to appear.
export const getEligibleSingleSeatMerchandisingVariant = (
  searchParameters: SearchParameters,
  searchProfile: SearchProfile,
  offer: SingleSeatOffer,
  experiment: SingleSeatExperiment
): SingleSeatMerchandisingVariantEnum | null =>
  isEligibleForSingleSeat(searchProfile, offer, experiment)
    ? getSingleSeatMerchandisingVariant(searchParameters, offer)
    : null;
